import {createElement, includes} from '../helpers/helpers';

const isWindows = includes(navigator.userAgent, 'Windows');

/**
 * Creates a panel component.
 */
export default class {

    constructor(options) {
        this._options = Object.assign({
            modal: false
        }, options);
    }

    /**
     * Sets the panel's title to a string of text.
     * @param {string} title - The title of the panel
     * @returns {Panel} Returns itself to allow for method chaining
     */
    setTitle(title) {
        const me = this,
            container = me._container;

        me._title = title;
        if (container) {
            container.querySelector('#panel-title').innerHTML = title;
        }

        return me;
    }

    /**
     * Sets the panel's content to the HTML provided as a string.
     * @param {string} html - A string representing HTML content for the panel
     * @returns {Panel} Returns itself to allow for method chaining
     */
    setHTML(html) {
        const me = this,
            container = me._container;

        me._html = html;
        if (container) {
            container.querySelector('#panel-content').innerHTML = html;
        }

        return me;
    }

    /**
     * Sets buttons on the panel's title.
     * @param {Array<HTMLElement>} buttons - An array of DOM elements to use as buttons
     *     on the title of the panel
     * @returns {Panel} Returns itself to allow for method chaining
     */
    setButtons(buttons) {
        const me = this,
            container = me._container;

        me._buttons = buttons;
        if (container) {
            const buttonGroup = container.querySelector('#panel-button-group'),
                children = buttonGroup.children;

            for (let i = children.length - 1; i > 0; i--) {
                buttonGroup.removeChild(children[i]);
            }
            for (const button of buttons || []) {
                buttonGroup.appendChild(button);
            }
        }

        return me;
    }

    /**
     * Makes the panel a draggable bottom sheet with three stops.
     * 탭은 기존대로 접힘/펼침을 오가고, 끌면 높이가 따라오다가 놓을 때 가장 가까운
     * 단으로 붙는다. 단이 셋인 이유는 33% 한 단만으로는 시간표를 보려면 지도를
     * 가리고 지도를 보려면 시간표를 못 보기 때문이다.
     * @param {HTMLElement} container - Panel container
     */
    _addSheetGestures(container) {
        const me = this,
            header = container.querySelector('#panel-header'),
            // 화면 높이 대비 비율. peek 은 제목 줄만 남긴다(높이는 CSS 가 정한다).
            stops = ['collapsed', '', 'full'];
        let startY, startHeight, dragging, moved;

        header.style.cursor = 'pointer';
        header.style.touchAction = 'none';

        const stopIndex = () => container.classList.contains('collapsed') ? 0 :
            container.classList.contains('full') ? 2 : 1;

        const apply = index => {
            container.classList.remove('collapsed', 'full');
            if (stops[index]) {
                container.classList.add(stops[index]);
            }
        };

        header.addEventListener('pointerdown', e => {
            dragging = true;
            moved = false;
            startY = e.clientY;
            startHeight = container.getBoundingClientRect().height;
            // 끄는 동안은 전환 애니메이션을 끈다. 켜두면 손가락을 따라오지 않고 늘어진다.
            container.style.transition = 'none';
            header.setPointerCapture(e.pointerId);
        });

        header.addEventListener('pointermove', e => {
            if (!dragging) {
                return;
            }
            const delta = startY - e.clientY;

            if (Math.abs(delta) > 5) {
                moved = true;
            }
            if (moved) {
                const max = me._map.container.getBoundingClientRect().height * .85;

                container.style.height = `${Math.max(50, Math.min(max, startHeight + delta))}px`;
            }
        });

        const end = () => {
            if (!dragging) {
                return;
            }
            dragging = false;
            container.style.transition = '';
            if (!moved) {
                // 움직이지 않았으면 탭이다. 기존 동작대로 접힘/펼침만 오간다.
                container.style.height = '';
                apply(stopIndex() === 0 ? 1 : 0);
                return;
            }

            // 놓은 높이에서 가장 가까운 단으로 붙인다
            const height = container.getBoundingClientRect().height,
                mapHeight = me._map.container.getBoundingClientRect().height,
                targets = [50, mapHeight * .33, mapHeight * .75];
            let best = 0;

            for (let i = 1; i < targets.length; i++) {
                if (Math.abs(targets[i] - height) < Math.abs(targets[best] - height)) {
                    best = i;
                }
            }
            container.style.height = '';
            apply(best);
        };

        header.addEventListener('pointerup', end);
        header.addEventListener('pointercancel', end);
    }

    /**
     * Adds the panel to a map.
     * @param {Map} map - The Mini Tokyo 3D map to add the panel to
     * @returns {Panel} Returns itself to allow for method chaining
     */
    addTo(map) {
        const me = this,
            options = me._options;

        me._map = map;

        if (options.modal) {
            const background = me._background = createElement('div', {
                className: 'modal-panel-background closed'
            }, map.container);

            background.addEventListener('click', () => {
                me.remove();
            });

            // 모달은 Escape 로도 닫혀야 한다 (WAI-ARIA APG dialog). 배경 클릭과 ×
            // 버튼만 있으면 키보드 사용자에게 빠져나갈 길이 없다. 여기 두면 모든
            // 모달 패널(노선 선택·추적 모드·정보)이 한 번에 같은 동작을 얻는다.
            // map.js 의 Escape 는 UI 숨김 해제만 하고 전파를 끊지 않으므로 겹치지 않는다.
            me._onKeyDown = event => {
                if (event.key === 'Escape' && me.isOpen()) {
                    me.remove();
                }
            };
            document.addEventListener('keydown', me._onKeyDown);
        }

        const container = me._container = createElement('div', {
            className: `panel closed ${options.className || ''}`,
            innerHTML: [
                '<div id="panel-header">',
                '<div id="panel-title"></div>',
                '<div id="panel-button-group" class="panel-button-group">',
                `<div id="panel-button" class="${options.modal ? 'close-button' : 'slide-button'}"></div>`,
                '</div>',
                '</div>',
                `<div id="panel-body"${isWindows ? ' class="windows"' : ''}>`,
                '<div id="panel-content"></div>',
                '</div>'
            ].join('')
        }, map.container);

        if (me._title) {
            me.setTitle(me._title);
        }
        if (me._html) {
            me.setHTML(me._html);
        }
        if (me._buttons) {
            me.setButtons(me._buttons);
        }

        if (options.modal) {
            container.querySelector('#panel-button').addEventListener('click', () => {
                me.remove();
            });
        } else {
            me._addSheetGestures(container);
        }

        requestAnimationFrame(() => {
            if (options.modal) {
                me._background.classList.remove('closed');
                container.style.height =
                    `min(calc(100% - 10px), ${container.querySelector('#panel-content').offsetHeight + 50}px)`;
            }
            container.classList.remove('closed');
        });

        return me;
    }

    /**
     * Removes the panel from a map.
     * @returns {Panel} Returns itself to allow for method chaining
     */
    remove() {
        const me = this,
            options = me._options,
            background = me._background,
            container = me._container;

        if (options.modal) {
            background.classList.add('closed');
            document.removeEventListener('keydown', me._onKeyDown);
            delete me._onKeyDown;
        }
        container.style.removeProperty('height');
        container.classList.add('closed');

        setTimeout(() => {
            if (options.modal) {
                background.parentNode.removeChild(background);
                delete me._background;
            }
            container.parentNode.removeChild(container);
            delete me._container;
            delete me._map;
        }, 300);

        return me;
    }

    /**
     * Checks if a panel is open.
     * @returns {boolean} True if the panel is open, false if it is closed
     */
    isOpen() {
        return !!this._map;
    }

}
